/**
 * Preparing a treatment's frames for its PDF.
 *
 * Frameset's CDN serves browsers but answers servers with a bot challenge, so
 * the server never fetches a frame. This runs in the user's browser instead:
 * fetch each frame, shrink it, and hand it over as a data URL with the PDF
 * request. Nothing is stored anywhere - the images live only in that PDF.
 *
 * The backend checks what arrives (treatment_service.image_data_url_ok): JPEG,
 * PNG or WebP only, at most 3 MB each.
 */

export const PDF_FRAME_MAX_WIDTH = 1600; // about 2.5x the printed width - sharp, not huge
export const PDF_FRAME_QUALITY = 0.82;
const FETCH_TIMEOUT_MS = 20000;
const CONCURRENCY = 4;

/** The size to draw at: never wider than `maxWidth`, never enlarged. */
export function scaledSize(width, height, maxWidth = PDF_FRAME_MAX_WIDTH) {
  if (!width || !height) return { width: 0, height: 0 };
  if (width <= maxWidth) return { width, height };
  return { width: maxWidth, height: Math.round((height * maxWidth) / width) };
}

/** Every frame in the treatment, once per reference id. */
export function framesToPrepare(shots) {
  return (shots || []).flatMap((s) => (s.references || []).map((r) => ({ id: r.id, url: r.image_url })));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** One frame as a JPEG data URL, or null if it can't be had. */
async function prepareFrame(url, maxWidth = PDF_FRAME_MAX_WIDTH) {
  const controller = new window.AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    // Frameset labels everything binary/octet-stream; decoding is the real check.
    const bitmap = await window.createImageBitmap(await response.blob());
    const { width, height } = scaledSize(bitmap.width, bitmap.height, maxWidth);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const jpeg = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', PDF_FRAME_QUALITY));
    return jpeg ? await blobToDataUrl(jpeg) : null;
  } catch {
    return null; // a site that refuses cross-origin reads, a timeout, not an image
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The treatment's link-preview image: its first frame, 1200px wide - what
 * iMessage, WhatsApp and Slack show. Prepared here for the same reason as the
 * PDF's frames: Frameset refuses the preview bots too.
 */
export const SHARE_IMAGE_MAX_WIDTH = 1200;
export const prepareShareImage = (url) => prepareFrame(url, SHARE_IMAGE_MAX_WIDTH);

/** The first frame of the first shot that has one. Twin of treatment_service.cover_url. */
export function coverUrl(shots) {
  for (const shot of shots || []) {
    const ref = (shot.references || []).find((r) => r.image_url);
    if (ref) return ref.image_url;
  }
  return null;
}

/**
 * Prepare every frame, a few at a time.
 * Returns { images: { referenceId: dataUrl }, failed: count }.
 */
export async function prepareFrames(shots, onProgress = () => {}) {
  const frames = framesToPrepare(shots);
  const images = {};
  let done = 0;
  let failed = 0;
  let next = 0;

  const worker = async () => {
    while (next < frames.length) {
      const frame = frames[next];
      next += 1;
      const dataUrl = await prepareFrame(frame.url);
      if (dataUrl) images[frame.id] = dataUrl;
      else failed += 1;
      done += 1;
      onProgress(done, frames.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, frames.length) }, worker));
  return { images, failed };
}
